/*! TM shipping-cue banner
 * Display-only. Reads /cart.js subtotal AND window.Shopify.country (Shopify's
 * own IP-geolocated country code, already computed for Markets/localization
 * on every storefront page load — no extra API call needed) to show progress
 * toward the UK £25 or international £75 free-shipping threshold.
 *
 * UPDATED 2026-09 (T3, launch fixes): live Shopify shipping settings now
 * match these thresholds (confirmed via Admin API before this change), so
 * the section is enabled by default. Currency-aware: thresholds and the
 * remaining-amount message are converted to the shopper's active currency
 * using window.Shopify.currency.rate (GBP-based rate; EU and US/CA markets
 * both have local currencies switched on) and formatted with that
 * currency's own symbol via Intl.NumberFormat, not a hardcoded £ sign.
 *
 * BUGFIX (T3 EUR/USD live verification, 2026-09-19): /cart.js returns
 * amounts already in the shopper's ACTIVE/presentment currency whenever a
 * market has local currencies switched on (confirmed live: a 5-token EUR
 * cart's total_price came back as 1620, i.e. EUR cents €16.20, not GBP
 * pence) — so the subtotal this file receives from refresh() is
 * active-currency pence already. The previous version re-multiplied that
 * value by window.Shopify.currency.rate a SECOND time (on top of the
 * threshold's own conversion), understating "remaining" in every non-GBP
 * currency: it computed (7500 - 1620) * rate = €69.96 instead of the
 * correct (7500 * rate) - 1620 = €73.03 on that same cart. Fixed by
 * treating the subtotal as already-active-currency throughout and
 * converting ONLY the fixed GBP-pence threshold — the same pattern
 * tbm-token-bundle.js already used correctly for the ladder banner
 * (see localSingleTokenPrice() there: "item.price is in the cart's
 * presentment currency already ... no further conversion needed").
 * GBP is unaffected (rate = 1 collapses the corrected formula to exactly
 * the original arithmetic), so GBP output stays byte-identical.
 *
 * Zone detection: window.Shopify.country === 'GB' → UK threshold, anything
 * else (including unknown/undetected) → international threshold. This errs
 * towards the higher, safer threshold for any ambiguous case rather than
 * under-promising or over-promising a UK-only rate to a non-UK visitor.
 *
 * Deliberately independent of tm-gift-cue.js and tbm-token-bundle.js:
 *   - different namespace (tm-shipping-cue) so neither can collide
 *   - different signal: cart.total_price (subtotal) + a zone lookup
 *   - NO discount object, NO cart/add calls, NO checkout interaction.
 *     Shopify's own checkout still calculates the real shipping cost;
 *     this banner never changes what the customer pays.
 *
 * Sibling to tm-gift-cue.js, using its "place once per drawer" fix (the
 * v3 fix from the 5 Jul position-fighting bug between tm-gift-cue and
 * tbm-token-bundle) from the start, rather than the earlier reactive
 * re-derivation pattern that caused that bug. A third banner reactively
 * re-asserting position against the same anchors would reintroduce the
 * exact same class of fight.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('tm-shipping-cue-data');
  if (!configEl) return;
  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn('[tm-shipping-cue] failed to parse data', e);
    return;
  }

  var UK_THRESHOLD_GBP_PENCE = CONFIG.uk_threshold_pence || 2500;
  var INTL_THRESHOLD_GBP_PENCE = CONFIG.intl_threshold_pence || 7500;
  var COPY_BELOW = (CONFIG.copy && CONFIG.copy.below) || 'Spend {remaining} more for free {zone} shipping.';
  var COPY_QUALIFIED = (CONFIG.copy && CONFIG.copy.qualified) || 'Free {zone} shipping unlocked.';

  var instances = new Set();
  var lastSubtotal = -1; // force first render

  function isUK() {
    return !!(window.Shopify && window.Shopify.country === 'GB');
  }

  function zoneLabel() {
    return isUK() ? 'UK' : 'international';
  }

  function thresholdGbpPence() {
    return isUK() ? UK_THRESHOLD_GBP_PENCE : INTL_THRESHOLD_GBP_PENCE;
  }

  // ---------- Currency helpers ----------
  // window.Shopify.currency is populated by Shopify's currency.js on any
  // storefront where a market has local currencies switched on (confirmed
  // live 2026-09: EU market and US/CA market both have localCurrencies:true,
  // base currency GBP). `.active` is the shopper's currently active currency
  // code; `.rate` is the conversion multiplier FROM the shop's base currency
  // (GBP here) TO that active currency. If Shopify.currency is absent
  // (e.g. a very old cached page before the object loads), fall back to GBP
  // with rate 1 — safe default, never shows a wrong number.
  function activeCurrencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency_code || 'GBP';
  }

  function currencyRateFromGbp() {
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    if (!rate || isNaN(rate) || rate <= 0) return 1;
    return rate;
  }

  // activePence (integer, already in the shopper's active currency) ->
  // formatted string in that currency, via Intl. No rate multiplication
  // here — the caller is responsible for converting GBP-pence figures
  // (like the fixed threshold) to active-currency pence BEFORE calling
  // this; a subtotal read from /cart.js is already active-currency and
  // must never be passed through a second conversion.
  function formatActiveMoney(activePence) {
    var code = activeCurrencyCode();
    var amount = activePence / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Intl currency code lookup can throw on an unrecognised code (defensive only —
      // Shopify.currency.active should always be a valid ISO 4217 code).
      return code + ' ' + amount.toFixed(2);
    }
  }

  // subtotalActivePence: the cart subtotal exactly as /cart.js reports it —
  // already in the shopper's active/presentment currency, per Shopify's own
  // Ajax Cart API behaviour on a multi-currency storefront. Only the fixed
  // GBP-pence threshold needs converting to that same currency before the
  // two can be compared like-for-like.
  function compute(subtotalActivePence) {
    var threshGbpPence = thresholdGbpPence();
    var zone = zoneLabel();
    var rate = currencyRateFromGbp();
    var threshActive = Math.round(threshGbpPence * rate);

    if (subtotalActivePence >= threshActive) {
      return { state: 'qualified', message: COPY_QUALIFIED.replace('{zone}', zone), progressPct: 100 };
    }
    var remainingActive = threshActive - subtotalActivePence;
    var message = COPY_BELOW.replace('{remaining}', formatActiveMoney(remainingActive)).replace('{zone}', zone);
    var pct = subtotalActivePence <= 0 ? 0 : (subtotalActivePence / threshActive) * 100;
    return { state: 'below', message: message, progressPct: pct };
  }

  function renderInstance(root, subtotalPence) {
    if (!root || !root.isConnected) return;
    var info = compute(subtotalPence);
    root.setAttribute('data-state', info.state);

    var msg = root.querySelector('[data-tm-ship-message]');
    if (msg) msg.textContent = info.message;

    var fill = root.querySelector('[data-tm-ship-progress-fill]');
    if (fill) fill.style.width = Math.max(0, Math.min(100, info.progressPct)) + '%';
  }

  function renderAll(subtotalPence) {
    lastSubtotal = subtotalPence;
    instances.forEach(function (el) {
      renderInstance(el, subtotalPence);
    });
  }

  if (!customElements.get('tm-shipping-cue')) {
    customElements.define(
      'tm-shipping-cue',
      class extends HTMLElement {
        connectedCallback() {
          instances.add(this);
          renderInstance(this, Math.max(lastSubtotal, 0));
        }
        disconnectedCallback() {
          instances.delete(this);
        }
      }
    );
  }

  var fetchInFlight = null;
  function refresh() {
    if (fetchInFlight) return fetchInFlight;
    fetchInFlight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        var subtotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
        renderAll(subtotal);
      })
      .catch(function () {})
      .finally(function () {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url = '';
      try {
        url = typeof arguments[0] === 'string' ? arguments[0] : arguments[0].url || '';
      } catch (e) {}
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        Promise.resolve(p).then(function () {
          setTimeout(refresh, 60);
        });
      }
      return p;
    };
  }

  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__tmsc_url = url;
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      var url = xhr.__tmsc_url || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        xhr.addEventListener('loadend', function () {
          setTimeout(refresh, 60);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  ['cart:updated', 'cart:refresh', 'cart-update', 'shopify:cart:updated', 'cart:change'].forEach(
    function (ev) {
      document.addEventListener(ev, function () {
        refresh();
      });
    }
  );

  // Re-render (without a new /cart.js fetch) when the shopper switches
  // currency via the localization/country selector, since that changes
  // window.Shopify.currency.rate without touching the cart itself.
  document.addEventListener('change', function (e) {
    var form = e.target && e.target.closest && e.target.closest('form[data-type="localization-form"], localization-form, form.localization-form');
    if (form) {
      setTimeout(function () {
        renderAll(Math.max(lastSubtotal, 0));
      }, 300);
    }
  });

  function getTemplate() {
    return document.getElementById('tm-shipping-cue-template');
  }

  function injectBefore(targetEl, wrapClass) {
    if (!targetEl || !targetEl.parentElement) return null;
    if (targetEl.parentElement.querySelector('.' + wrapClass)) return null;
    var tpl = getTemplate();
    if (!tpl) return null;
    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    wrap.appendChild(tpl.content.cloneNode(true));
    targetEl.parentElement.insertBefore(wrap, targetEl);
    return wrap;
  }

  function injectAfter(targetEl, wrapClass) {
    if (!targetEl || !targetEl.parentElement) return null;
    var existing = targetEl.parentElement.querySelector('.' + wrapClass);
    if (existing) return existing;
    var tpl = getTemplate();
    if (!tpl) return null;
    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    wrap.appendChild(tpl.content.cloneNode(true));
    if (targetEl.nextSibling) {
      targetEl.parentElement.insertBefore(wrap, targetEl.nextSibling);
    } else {
      targetEl.parentElement.appendChild(wrap);
    }
    return wrap;
  }

  // Place-once pattern from the start (this is the FIXED v3 approach used
  // by tm-gift-cue.js after the 5 Jul position-fighting bug, not the
  // original reactive-recheck pattern that caused it). T3 (2026-09) gives
  // this banner its OWN fixed slot in the drawer markup (a dedicated anchor
  // element in cart-drawer.liquid, not a shared "before summary" race with
  // tbm-token-bundle / tm-gift-cue) — see cart-drawer.liquid for the anchor.
  function placeDrawerBanner(drawer) {
    var wrap = drawer.querySelector('.tm-shipping-cue-drawer-wrap');
    if (!wrap) return false;

    if (drawer.dataset.tmShipPlaced === 'true' && wrap.isConnected && drawer.contains(wrap)) {
      return false;
    }

    // Preferred: the dedicated fixed-slot anchor added in T3.
    var anchor = drawer.querySelector('[data-tm-shipping-cue-slot]');
    if (anchor) {
      anchor.appendChild(wrap);
      drawer.dataset.tmShipPlaced = 'true';
      return true;
    }

    // Fallback (older markup without the anchor): previous behaviour.
    var content =
      drawer.querySelector('.cart-drawer__content') ||
      drawer.querySelector('.cart-drawer__inner');
    if (!content) return false;

    var summary = content.querySelector('.cart-drawer__summary');
    if (summary) {
      content.insertBefore(wrap, summary);
      drawer.dataset.tmShipPlaced = 'true';
      return true;
    }
    var items =
      content.querySelector('.cart-drawer__items') ||
      content.querySelector('cart-items-component');
    if (items && items.parentElement) {
      var parent = items.parentElement;
      parent.insertBefore(wrap, items.nextSibling);
      drawer.dataset.tmShipPlaced = 'true';
      return true;
    }
    return false;
  }

  function placeAllDrawerBanners() {
    var moved = false;
    document
      .querySelectorAll(
        'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
      )
      .forEach(function (drawer) {
        if (placeDrawerBanner(drawer)) moved = true;
      });
    return moved;
  }

  function injectCartPage() {
    var items = document.querySelector('.cart-page__items');
    if (items) injectBefore(items, 'tm-shipping-cue-cart-wrap');
  }

  function injectCartDrawer() {
    var drawers = document.querySelectorAll(
      'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
    );
    drawers.forEach(function (drawer) {
      var anchor = drawer.querySelector('[data-tm-shipping-cue-slot]');
      if (anchor && !anchor.querySelector('.tm-shipping-cue-drawer-wrap')) {
        var tpl = getTemplate();
        if (tpl) {
          var wrap = document.createElement('div');
          wrap.className = 'tm-shipping-cue-drawer-wrap';
          wrap.appendChild(tpl.content.cloneNode(true));
          anchor.appendChild(wrap);
          drawer.dataset.tmShipPlaced = 'true';
        }
        return;
      }

      var content =
        drawer.querySelector('.cart-drawer__content') ||
        drawer.querySelector('.cart-drawer__inner');
      var summary = content && content.querySelector('.cart-drawer__summary');
      var items =
        drawer.querySelector('.cart-drawer__items') ||
        drawer.querySelector('cart-items-component');

      if (summary) {
        injectBefore(summary, 'tm-shipping-cue-drawer-wrap');
      } else if (items) {
        injectAfter(items, 'tm-shipping-cue-drawer-wrap');
      }

      placeDrawerBanner(drawer);
    });
  }

  function isTmShipNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.tagName && node.tagName.toLowerCase() === 'tm-shipping-cue') return true;
    if (node.classList) {
      if (
        node.classList.contains('tm-shipping-cue') ||
        node.classList.contains('tm-shipping-cue-cart-wrap') ||
        node.classList.contains('tm-shipping-cue-drawer-wrap')
      ) {
        return true;
      }
    }
    return node.closest && node.closest('tm-shipping-cue, .tm-shipping-cue-cart-wrap, .tm-shipping-cue-drawer-wrap');
  }

  function mutationIsRelevant(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (!isTmShipNode(m.target)) {
        if (m.addedNodes && m.addedNodes.length) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            if (!isTmShipNode(m.addedNodes[j])) return true;
          }
        }
        if (m.removedNodes && m.removedNodes.length) {
          for (var k = 0; k < m.removedNodes.length; k++) {
            if (!isTmShipNode(m.removedNodes[k])) return true;
          }
        }
        if (!m.addedNodes.length && !m.removedNodes.length) return true;
      }
    }
    return false;
  }

  function tryInject() {
    injectCartPage();
    injectCartDrawer();
  }

  function ensureInjected() {
    var cartWrapBefore = document.querySelector('.tm-shipping-cue-cart-wrap');
    var drawerWrapBefore = document.querySelector('.tm-shipping-cue-drawer-wrap');

    tryInject();
    var movedDrawer = placeAllDrawerBanners();

    var cartWrapAfter = document.querySelector('.tm-shipping-cue-cart-wrap');
    var drawerWrapAfter = document.querySelector('.tm-shipping-cue-drawer-wrap');

    var reinjected =
      (!cartWrapBefore && cartWrapAfter) ||
      (!drawerWrapBefore && drawerWrapAfter) ||
      (cartWrapBefore && !cartWrapBefore.isConnected) ||
      (drawerWrapBefore && !drawerWrapBefore.isConnected) ||
      movedDrawer;

    if (reinjected) {
      renderAll(Math.max(lastSubtotal, 0));
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    ensureInjected();
    refresh();
  });

  if (document.readyState !== 'loading') {
    ensureInjected();
    refresh();
  }

  [50, 200, 500, 1200, 3000].forEach(function (ms) {
    setTimeout(ensureInjected, ms);
  });

  var observerThrottle = null;
  var observer = new MutationObserver(function (mutations) {
    if (!mutationIsRelevant(mutations)) return;
    if (observerThrottle) return;
    observerThrottle = setTimeout(function () {
      observerThrottle = null;
      ensureInjected();
    }, 120);
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
